/*
  Neroli Theme — product-v2.js

  Variant selection via the Section Rendering API.

  Why server re-render rather than patching values in JS: price formatting,
  compare-at pairing, sale badges, unit price, inventory copy, Shop Pay
  Installments and pickup availability all depend on merchant settings and
  Shopify-side data that can't be reproduced faithfully in the browser. The
  previous client-side approach updated the price but left the compare-at
  price, the sale badge and the discount badge showing the *previous*
  variant's numbers, and formatted money with the browser locale instead of
  the shop's money format.

  The <form> itself is deliberately never replaced. Re-rendering it would
  destroy the dynamic checkout button that Shopify's own script has already
  initialized, and would reset the quantity the customer typed. Only the
  regions marked [data-vr] are swapped; the add-to-cart button state is
  toggled in place.
*/

const VARIANT_REGIONS = [
  '[data-vr="badges"]',
  '[data-vr="price"]',
  '[data-vr="inventory"]',
  '[data-vr="installments"]',
  '[data-vr="pickup"]',
  '[data-vr="sticky-price"]',
  '[data-vr="sticky-variant"]',
  '[data-vr="media"]',
];

class ProductInfo extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    const jsonScript =
      this.querySelector('[data-product-json]') ||
      this.closest('.main-product')?.querySelector('[data-product-json]') ||
      document.querySelector('[data-product-json]');
    if (!jsonScript) return;

    try {
      this.productData = JSON.parse(jsonScript.textContent);
    } catch (error) {
      return;
    }

    this.sectionId = this.dataset.sectionId;
    this.productUrl = this.dataset.productUrl;

    this.variantIdInput = this.querySelector('[data-product-form-variant-id]');
    /* Must be scoped to `input`. The <fieldset> carries data-option-position
       too, and a disabled fieldset disables every control inside it — which
       struck through and disabled every option on the page. */
    this.optionInputs = Array.from(this.querySelectorAll('input[data-option-position]'));
    this.optionFieldsets = Array.from(this.querySelectorAll('[data-variant-option]'));
    this.addToCartButton = this.querySelector('[data-add-to-cart]');
    this.addToCartText = this.querySelector('[data-add-to-cart-text]');
    this.stickyButton = this.querySelector('[data-sticky-add]');

    this.optionInputs.forEach((input) => {
      input.addEventListener('change', () => this.handleOptionChange());
    });

    this.updateOptionAvailability();
  }

  /* ---- Selection ---- */

  getSelectedOptions() {
    const positions = [...new Set(this.optionInputs.map((el) => el.dataset.optionPosition))];
    return positions
      .sort((a, b) => Number(a) - Number(b))
      .map((position) => {
        const checked = this.optionInputs.find(
          (el) => el.dataset.optionPosition === position && el.checked
        );
        return checked ? checked.value : null;
      });
  }

  findMatchingVariant(selectedOptions) {
    return this.productData.variants.find((variant) =>
      variant.options.every((value, index) => value === selectedOptions[index])
    );
  }

  /* A value is offered when at least one *available* variant exists that uses
     it alongside everything else currently selected. Values that can't be
     reached at all are disabled; the CSS strikes them through. */
  updateOptionAvailability() {
    const selected = this.getSelectedOptions();

    this.optionInputs.forEach((input) => {
      const index = Number(input.dataset.optionPosition) - 1;
      const reachable = this.productData.variants.some((variant) => {
        if (variant.options[index] !== input.value) return false;
        return variant.options.every(
          (value, i) => i === index || selected[i] == null || value === selected[i]
        );
      });
      const purchasable = this.productData.variants.some((variant) => {
        if (variant.options[index] !== input.value) return false;
        if (!variant.available) return false;
        return variant.options.every(
          (value, i) => i === index || selected[i] == null || value === selected[i]
        );
      });
      input.disabled = !reachable;
      input.closest('.variant-option__value')?.classList.toggle('is-unavailable', !purchasable);
    });
  }

  updateLegends() {
    this.optionFieldsets.forEach((fieldset) => {
      const label = fieldset.querySelector('[data-selected-option-value]');
      const checked = fieldset.querySelector('input[type="radio"]:checked');
      if (label && checked) label.textContent = checked.value;
    });
  }

  /* ---- Change handling ---- */

  handleOptionChange() {
    const variant = this.findMatchingVariant(this.getSelectedOptions());

    this.updateLegends();
    this.updateOptionAvailability();

    if (!variant) {
      this.setButtonState(false, true);
      return;
    }

    if (this.variantIdInput) this.variantIdInput.value = variant.id;
    this.setButtonState(variant.available, false, variant.id);
    this.updateUrl(variant.id);
    this.swapMedia(variant);

    document.dispatchEvent(
      new CustomEvent('variant:change', {
        detail: {
          variantId: variant.id,
          featuredMediaId: variant.featured_media ? variant.featured_media.id : null,
        },
      })
    );

    this.renderVariant(variant.id);
  }

  /**
   * Swaps a single [data-vr="media"] image straight from the variant JSON.
   *
   * The server re-render below also replaces this region, but going over the
   * network to change a picture the browser already knows about is both
   * slower and more fragile: it depends on the Section Rendering API
   * honouring ?variant=, on the section file being current on the CDN, and
   * on the request completing at all. The product page gallery has always
   * worked this way, from the same JSON, which is why it swaps reliably.
   *
   * Sections with a full gallery have no [data-vr="media"] and are untouched
   * here — product-gallery.js handles those through the variant:change event.
   */
  swapMedia(variant) {
    const image = this.querySelector('[data-vr="media"] img');
    if (!image) return;

    const source = variant.featured_image;
    if (!source || !source.src) return;

    const width = image.getAttribute('width') || 500;
    const separator = source.src.includes('?') ? '&' : '?';
    const next = `${source.src}${separator}width=${width}`;

    // srcset would otherwise win over the new src and keep showing the old
    // image at every candidate width.
    image.removeAttribute('srcset');
    image.removeAttribute('sizes');
    image.src = next;
    if (source.alt) image.alt = source.alt;
  }

  setButtonState(available, unavailableCombination, variantId) {
    if (this.addToCartButton) this.addToCartButton.disabled = !available;
    if (this.stickyButton) this.stickyButton.disabled = !available;
    if (!this.addToCartText) return;

    const { availableLabel, soldOutLabel, unavailableLabel, preorderLabel, preorderVariants } =
      this.addToCartText.dataset;

    /* A backordered variant is "available" as far as Shopify is concerned,
       so availability alone cannot distinguish it from a stocked one. The
       ids are listed server-side, where the inventory fields are readable. */
    const preorderIds = (preorderVariants || '').split(',').filter(Boolean);
    const isPreorder = Boolean(variantId) && preorderIds.includes(String(variantId));

    let label;
    if (unavailableCombination) {
      label = unavailableLabel || soldOutLabel || '';
    } else if (!available) {
      label = soldOutLabel;
    } else if (isPreorder && preorderLabel) {
      label = preorderLabel;
    } else {
      label = availableLabel;
    }

    this.addToCartText.textContent = label;
    if (this.stickyButton) this.stickyButton.textContent = label;

    /* Lets the note and any badge react without re-querying inventory. */
    this.toggleAttribute('data-preorder', isPreorder);
  }

  updateUrl(variantId) {
    if (!window.history || !window.history.replaceState) return;

    // Only when this element is the page's own product. In a quick-add
    // dialog on a collection page the variant belongs to a different
    // product, and stamping it onto the collection URL leaves a stale
    // parameter behind after the dialog closes.
    if (this.dataset.updateUrl === 'false') return;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url);
  }

  /* ---- Server re-render ---- */

  async renderVariant(variantId) {
    if (!this.sectionId || !this.productUrl) return;

    // A customer clicking through sizes quickly fires several requests; only
    // the newest one may write to the DOM, or an earlier slower response
    // overwrites it with a stale variant.
    this.abortController?.abort();
    this.abortController = new AbortController();

    this.setAttribute('aria-busy', 'true');

    const url = `${this.productUrl}?variant=${variantId}&section_id=${this.sectionId}`;

    try {
      const response = await fetch(url, { signal: this.abortController.signal });
      if (!response.ok) return;

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');

      VARIANT_REGIONS.forEach((selector) => {
        const incoming = html.querySelectorAll(selector);
        const current = this.querySelectorAll(selector);
        const count = Math.min(incoming.length, current.length);
        for (let i = 0; i < count; i += 1) {
          current[i].innerHTML = incoming[i].innerHTML;
        }
      });

      document.dispatchEvent(
        new CustomEvent('variant:rendered', { detail: { variantId, sectionId: this.sectionId } })
      );
    } catch (error) {
      if (error.name !== 'AbortError') {
        // Leave the previously rendered values in place rather than blanking
        // the panel — the form still carries the correct variant id.
        console.warn('Neroli: variant render failed', error);
      }
    } finally {
      this.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('product-info')) {
  customElements.define('product-info', ProductInfo);
}

/*
  Ask a question modal. <dialog>.showModal() gives us the focus trap, Escape to
  close and focus restoration for free — no custom trap to maintain.

  Shopify's contact form posts and reloads the page, so a submitted form comes
  back with the success or error state already rendered. Reopening the dialog
  on load is the only way the customer sees it.
*/
class QuestionModal extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.dialog = this.querySelector('[data-question-modal]');
    if (!this.dialog || typeof this.dialog.showModal !== 'function') return;

    document.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-question-open]');
      if (!opener) return;
      if (opener.getAttribute('aria-controls') !== this.dialog.id) return;
      this.dialog.showModal();
    });

    this.querySelector('[data-question-close]')?.addEventListener('click', () => this.dialog.close());

    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });

    if (this.dialog.querySelector('[data-question-success]') || this.dialog.querySelector('.question-modal__error')) {
      this.dialog.showModal();
    }
  }
}

if (!customElements.get('question-modal')) {
  customElements.define('question-modal', QuestionModal);
}

// Gift card recipient fields. Hidden inputs would still submit empty line
// item properties, so the fieldset is disabled while collapsed.
function syncGiftCardFields(root) {
  root.querySelectorAll('[data-gift-card-toggle]').forEach((toggle) => {
    const fields = toggle.closest('gift-card-recipient')?.querySelector('[data-gift-card-fields]');
    if (!fields) return;
    fields.hidden = !toggle.checked;
    fields.querySelectorAll('input, textarea').forEach((field) => {
      field.disabled = !toggle.checked;
    });
  });
}

document.addEventListener('change', (event) => {
  if (event.target.matches('[data-gift-card-toggle]')) syncGiftCardFields(document);
});
document.addEventListener('DOMContentLoaded', () => syncGiftCardFields(document));

// Quantity +/- for the PRODUCT FORM only. Cart line items carry
// data-cart-quantity-input and belong to cart.js; without this guard both
// scripts fire on one click and the quantity jumps by two.
document.addEventListener('click', (event) => {
  const increase = event.target.closest('[data-quantity-increase]');
  const decrease = event.target.closest('[data-quantity-decrease]');
  if (!increase && !decrease) return;

  const wrapper = (increase || decrease).closest('[data-quantity-selector]');
  const input = wrapper?.querySelector('[data-quantity-input]');
  if (!input) return;
  if (input.matches('[data-cart-quantity-input]')) return;

  const min = Number(input.min) || 1;
  const current = Number(input.value) || min;
  input.value = increase ? current + 1 : Math.max(min, current - 1);
  input.dispatchEvent(new Event('change', { bubbles: true }));
});
